import {
  useAuthorizeIndividualLegalEntityBasisTheorySessionMutation,
  useGetIndividualEntityQuery,
} from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceId } from 'hooks/useWorkspace';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { FragmentEnv } from 'utils/env';
import { getResultOrError } from 'utils/errors';
import type { ITextElement, Token } from '@basis-theory/react-elements';
import {
  BasisTheoryProvider,
  useBasisTheory,
} from '@basis-theory/react-elements';
import { useQuery } from '@tanstack/react-query';

import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { ColorModeContext } from '@fragment/ui/src/components/ColorMode/ColorModeContext';
import { Logo } from '@fragment/ui/src/components/Logo/Logo';

import { NoticeView } from './NoticeView';
import {
  BasisTheoryTextElement,
  FormLoading,
  OnboardingCheckBox,
} from './SharedUI';

const PersonCDDLoadingView = () => (
  <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
    <Logo />
    <FormLoading />
  </div>
);

type PersonCDDViewProps = {
  entityId: string;
};

type PersonCDDFormProps = {
  token: Token;
  updateToken: (id: string, ref: React.RefObject<ITextElement>) => void;
  isDarkMode: boolean;
  onDataSubmitted: () => void;
};

const PersonCDDForm = ({
  token,
  updateToken,
  isDarkMode,
  onDataSubmitted,
}: PersonCDDFormProps) => {
  const [isOwner, setIsOwner] = useState(false);

  return (
    <div className="flex flex-col h-fit gap-y-f2">
      <span className="text-main">
        The following information is required to complete your onboarding to our
        Payments Platform.
      </span>
      <BasisTheoryTextElement
        id="name"
        label="Name"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="title"
        label="Title"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="taxIdSsn"
        label="Tax ID / SSN"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="email"
        label="Email"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="phoneNumber"
        label="Phone Number"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="dateOfBirth"
        label="Date of Birth"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="addressLine1"
        label="Address Line 1"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="addressLine2"
        label="Address Line 2"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="city"
        label="City"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="state"
        label="State"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <BasisTheoryTextElement
        id="zipCode"
        label="Zip Code"
        update={updateToken}
        token={token}
        isDarkMode={isDarkMode}
      />
      <OnboardingCheckBox
        checked={isOwner}
        label="I own more than a 25% stake in the company."
        onClick={() => setIsOwner(!isOwner)}
      />
      <PrimaryButtonV2 onClick={onDataSubmitted}>Submit</PrimaryButtonV2>
    </div>
  );
};

const PersonCDDSubmittedView = () => (
  <div className="flex flex-col h-fit gap-y-f2">
    <span>Your information has been submitted for review.</span>
  </div>
);

type PersonCDDStatus = 'submitted' | 'incomplete';

const deriveStatus = (entityStatus: string): PersonCDDStatus =>
  entityStatus === 'pending-submission' ? 'incomplete' : 'submitted';

const PersonCDDViewInner = ({ entityId }: PersonCDDViewProps) => {
  const workspaceId = useWorkspaceId();
  const { context: internalApiCtx } = useInternalApiContext();
  const { mode, systemIsDark } = useContextSafely(ColorModeContext);
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemIsDark);

  // fetch entity
  const [{ data: entityData, error: entityError, fetching: isFetchingEntity }] =
    useGetIndividualEntityQuery({
      variables: { workspaceId, entityId },
      context: internalApiCtx,
    });

  const { result: entityResult, isError: isEntityError } = getResultOrError(
    entityData?.individualEntity,
    entityError
  );

  const piiDataTokenId = useMemo(
    () => entityResult?.individualEntity.piiDataTokenId,
    [entityResult]
  );

  // authorize basis theory session
  const { bt } = useBasisTheory();
  const [, authorizeSession] =
    useAuthorizeIndividualLegalEntityBasisTheorySessionMutation();

  const {
    data: sessionKey,
    error: sessionError,
    isFetching: isFetchingSession,
  } = useQuery({
    queryKey: ['bt-session-person', workspaceId, entityId],
    enabled: bt != null,
    retry: 3,
    queryFn: async () => {
      const { sessionKey: key, nonce } = await bt!.sessions.create();

      const authResult = await authorizeSession(
        { workspaceId, entityId, nonce },
        internalApiCtx
      );

      if (authResult.error) {
        throw new Error(authResult.error.message);
      }

      const data =
        authResult.data?.authorizeIndividualLegalEntityBasisTheorySession;
      if (!data) {
        throw new Error('No response from server');
      }

      if (
        data.__typename === 'BadRequestError' ||
        data.__typename === 'InternalError'
      ) {
        throw new Error(data.message);
      }

      if (!data.success) {
        throw new Error('Session authorization failed');
      }

      return key;
    },
  });

  // retrieve the PII token
  const {
    data: token,
    error: tokenError,
    isFetching: isFetchingToken,
  } = useQuery({
    queryKey: ['token', piiDataTokenId],
    enabled: bt != null && sessionKey != null && piiDataTokenId != null,
    retry: 3,
    queryFn: async () =>
      bt!.tokens.retrieve(piiDataTokenId!, {
        apiKey: sessionKey!,
      }),
  });

  const updateToken = useCallback(
    (id: string, ref: React.RefObject<ITextElement>) => {
      if (bt && piiDataTokenId && ref.current) {
        bt.tokens.update(piiDataTokenId, {
          data: {
            [id]: ref.current,
          },
        });
      }
    },
    [bt, piiDataTokenId]
  );

  const [statusOverride, setStatusOverride] = useState<PersonCDDStatus | null>(
    null
  );

  const entityStatus = entityResult?.individualEntity.status;
  const status =
    statusOverride ??
    (entityStatus ? deriveStatus(entityStatus) : 'incomplete');

  const statusView = useMemo(
    () =>
      match<PersonCDDStatus>(status)
        .with('submitted', () => <PersonCDDSubmittedView />)
        .with('incomplete', () => (
          <PersonCDDForm
            token={token!}
            updateToken={updateToken}
            isDarkMode={isDarkMode}
            onDataSubmitted={() => setStatusOverride('submitted')}
          />
        ))
        .exhaustive(),
    [status, token, updateToken, isDarkMode]
  );

  if (isEntityError || sessionError || tokenError) {
    return (
      <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
        <Logo />
        <span className="text-main">
          Unable to load onboarding data. Please check the link and try again.
        </span>
      </div>
    );
  }

  if (isFetchingEntity || isFetchingSession || isFetchingToken || !token) {
    return <PersonCDDLoadingView />;
  }

  return (
    <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
      <Logo />
      {statusView}
    </div>
  );
};

export const PersonCDDView = ({ entityId }: PersonCDDViewProps) => {
  const { dashboardApiKey } = FragmentEnv.basisTheory!;
  const { bt } = useBasisTheory(dashboardApiKey);
  const [agreedToCIP, setAgreedToCIP] = useState(false);
  if (!dashboardApiKey) {
    return <div>Basis Theory is not configured for this environment.</div>;
  }

  if (!agreedToCIP) {
    return (
      <div className="flex flex-col h-fit gap-y-f2 p-f2 max-w-f56">
        <Logo />
        <NoticeView
          onboardingState={{}}
          setOnboardingField={() => {}}
          onNext={() => setAgreedToCIP(true)}
        />
      </div>
    );
  }

  if (!bt) {
    return <PersonCDDLoadingView />;
  }

  return (
    <BasisTheoryProvider bt={bt}>
      <Suspense fallback={<PersonCDDLoadingView />}>
        <PersonCDDViewInner entityId={entityId} />
      </Suspense>
    </BasisTheoryProvider>
  );
};
